// ============================================
// Tab.js
// WAI-ARIA 準拠のタブコンポーネント
//
// 機能:
//   - クリックでタブを切り替える
//   - 矢印キー（←→）でフォーカス移動
//   - 1 ページ内に複数のタブグループを設置可能
//
// アクセシビリティ:
//   - role="tablist" / role="tab" / role="tabpanel" を使用
//   - aria-selected でアクティブタブを示す
//   - aria-controls / id でタブとパネルを紐付ける
//
// ============================================
// HTML 構造例:
// ============================================
//
// <!-- タブグループ全体を .js-tab で囲む（複数設置可） -->
// <div class="js-tab">
//
//   <!-- タブボタン一覧 -->
//   <div role="tablist" class="js-tab__trigger" aria-label="コンテンツタブ">
//
//     <!-- 最初のタブは aria-selected="true"、tabindex 省略（デフォルト 0） -->
//     <button
//       role="tab"
//       class="js-tab__trigger-item"
//       aria-selected="true"
//       aria-controls="tab-panel-1"
//     >Tab 1</button>
//
//     <!-- 非アクティブタブは aria-selected="false" かつ tabindex="-1" -->
//     <button
//       role="tab"
//       class="js-tab__trigger-item"
//       aria-selected="false"
//       aria-controls="tab-panel-2"
//       tabindex="-1"
//     >Tab 2</button>
//
//     <button
//       role="tab"
//       class="js-tab__trigger-item"
//       aria-selected="false"
//       aria-controls="tab-panel-3"
//       tabindex="-1"
//     >Tab 3</button>
//   </div>
//
//   <!-- タブコンテンツ -->
//   <!-- 最初のパネルのみ表示（他は hidden 属性で非表示） -->
//   <div id="tab-panel-1" role="tabpanel" class="js-tab__content-item">
//     <p>Tab 1 の内容</p>
//   </div>
//   <div id="tab-panel-2" role="tabpanel" class="js-tab__content-item" hidden>
//     <p>Tab 2 の内容</p>
//   </div>
//   <div id="tab-panel-3" role="tabpanel" class="js-tab__content-item" hidden>
//     <p>Tab 3 の内容</p>
//   </div>
//
// </div>
//
// CSS 例:
//   .js-tab__content-item[hidden] { display: none; }
// ============================================

import { bindOnce } from '../utils/bindOnce';

// ============================================
// 設定値
// ============================================
const CONFIG = {
  // タブグループの外側ラッパーのセレクター
  groupSelector: '.js-tab',

  // タブリスト（tablist）のセレクター
  triggerListSelector: '.js-tab__trigger',

  // 各タブボタン（tab role）のセレクター
  tabSelector: '.js-tab__trigger-item',

  // 各タブパネル（tabpanel）のセレクター
  panelSelector: '.js-tab__content-item',
};

// ============================================
// tabFunc — ページ内の全タブグループを初期化する
// ============================================
function tabFunc() {
  const groups = document.querySelectorAll('.c-tab01');
  if (groups.length === 0) return;

  // グループごとに独立して初期化する（複数タブグループの共存をサポート）
  groups.forEach((group) => initTabGroup(group));
  // 1つのタブグループを初期化する
  function initTabGroup(group) {
    const tabList = group.querySelector(CONFIG.triggerListSelector);
    const tabs = Array.from(group.querySelectorAll(CONFIG.tabSelector));
    const panels = Array.from(group.querySelectorAll(CONFIG.panelSelector));
  
    if (!tabList || tabs.length === 0 || panels.length === 0) return;
  
    // タブをクリックしたときの切り替え処理を登録する
    bindClickEvents(tabs, panels);
  
    // 矢印キーによるキーボード操作を登録する
    bindKeyEvents(tabList, tabs, panels);
  }
  
  // クリックイベント: タブをクリックして切り替える
  function bindClickEvents(tabs, panels) {
    tabs.forEach((tab) => {
      // バインド済みチェック（二重登録防止）
      if (!bindOnce(tab, 'tabClick')) return;
  
      tab.addEventListener('click', () => {
        // すでにアクティブなタブはクリックしても何もしない
        if (tab.getAttribute('aria-selected') === 'true') return;
  
        activateTab(tab, tabs, panels);
      });
    });
  }
  
  // キーボードイベント: ←→ キーでタブを移動する
  function bindKeyEvents(tabList, tabs, panels) {
    // バインド済みチェック（二重登録防止）
    if (!bindOnce(tabList, 'tabKey')) return;
  
    tabList.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
  
      e.preventDefault(); // ←→ キーでのスクロールを防ぐ
  
      // 現在フォーカス中のタブを起点にする。
      // （変数でインデックスを保持するとクリック切り替え時に起点がずれるため、
      //   毎回 DOM の実際のフォーカス位置から算出する）
      let tabFocus = tabs.indexOf(document.activeElement);
      if (tabFocus === -1) {
        // フォーカスがタブ上にない場合は選択中タブを起点にする
        tabFocus = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
        if (tabFocus === -1) tabFocus = 0;
      }
  
      // フォーカスを移動する方向を計算する（端に達したら折り返す）
      if (e.key === 'ArrowLeft') {
        tabFocus = tabFocus <= 0 ? tabs.length - 1 : tabFocus - 1;
      } else {
        tabFocus = tabFocus >= tabs.length - 1 ? 0 : tabFocus + 1;
      }
  
      // 移動先のタブをアクティブにしてフォーカスをセット
      activateTab(tabs[tabFocus], tabs, panels);
      tabs[tabFocus].focus();
    });
  }
  
  // タブを切り替えるコアロジック
  function activateTab(targetTab, tabs, panels) {
    // すべてのタブを非アクティブ状態にリセット
    tabs.forEach((tab) => {
      tab.setAttribute('aria-selected', 'false');
      tab.setAttribute('tabindex', '-1'); // キーボードのフォーカス順から外す
    });
  
    // 対象タブをアクティブ化
    targetTab.setAttribute('aria-selected', 'true');
    targetTab.setAttribute('tabindex', '0'); // フォーカス可能な状態に戻す
  
    // パネルの表示/非表示を切り替える
    const targetPanelId = targetTab.getAttribute('aria-controls');
    panels.forEach((panel) => {
      if (panel.id === targetPanelId) {
        panel.removeAttribute('hidden'); // 対象パネルを表示
      } else {
        panel.setAttribute('hidden', ''); // 他のパネルを非表示
      }
    });
  }
}


export { tabFunc };
